import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Cloud,
  Server,
  Wallet,
  Lightbulb,
  LayoutDashboard,
  Plus,
  Upload,
  Pencil,
  Trash2,
  Eye,
  Settings2,
  FileSpreadsheet,
  ArrowUpDown,
} from 'lucide-react';
import { PageHeader } from '../../components/shared/PageHeader';
import { Pagination } from '../../components/shared/Pagination';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { ListTabBar, ListToolbar } from '../../components/shared/ListTabBar';
import { FilterChipsBar, FilterChip } from '../../components/shared/FilterChipsBar';
import { TableLoadingBar, TABLE_DIM_CLASS } from '../../components/shared/TableLoadingBar';
import { ResponsiveDataView } from '../../components/shared/ResponsiveDataView';
import ActionDropdown, { ActionItem } from '../../components/Common/ActionDropdown';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { FormField } from '../../components/shared/FormField';
import { Textarea } from '../../components/ui/textarea';
import { MultiSelectOption } from '../../components/shared/MultiSelect';
import {
  useManagementTableState,
  ManagementTableState,
} from '../../hooks/useManagementTableState';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import { useCurrency } from '../../context/CurrencyContext';
import { exportToCsv } from '../../lib/exportCsv';
import { formatDecimal } from '../../lib/formatNumber';
import { cn } from '../../lib/utils';
import { departmentsService } from '../../services/lookupService';
import { userService } from '../../services/userService';
import { currencyService } from '../../services/currencyService';
import { masterService, Vendor } from '../../services/masterService';
import cloudAssetService, {
  CloudEnvironment,
  CloudTier,
  CloudResource,
  CloudResourceQuery,
  CloudResourceType,
  CloudStatistics,
  CLOUD_PROVIDER_STATE_LABELS,
} from '../../services/cloudAssetService';
import CloudResourceFormDialog from './CloudResourceFormDialog';
import CloudResourceDetailModal from './CloudResourceDetailModal';
import CloudImportDialog from './CloudImportDialog';
import CloudEnvironmentsTab from './CloudEnvironmentsTab';
import CloudOverviewTab from './CloudOverviewTab';
import CloudCostTab from './CloudCostTab';
import { useCloudProviders } from './useCloudProviders';
import CloudRecommendationsTab from './CloudRecommendationsTab';
import {
  CloudAdvancedFilters,
  CloudAdvancedFilterState,
  EMPTY_CLOUD_FILTERS,
} from './CloudAdvancedFilters';

const EMPTY_STATS: CloudStatistics = {
  total: 0,
  active: 0,
  stopped: 0,
  unassigned: 0,
  environments: 0,
};

/** Table/card-list empty state — distinguishes "nothing recorded yet" from
 * "this search/filter narrowed the results to zero". */
const EmptyResourcesState: React.FC<{
  hasFilters: boolean;
  onClearFilters: () => void;
}> = ({ hasFilters, onClearFilters }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-10 text-center text-sm text-muted-foreground">
    <p>
      {hasFilters
        ? 'No cloud resources match the selected filters.'
        : 'No cloud resources found. Add one, or import a spreadsheet to populate the register.'}
    </p>
    {hasFilters && (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="cursor-pointer"
        onClick={onClearFilters}
      >
        Clear Filters
      </Button>
    )}
  </div>
);

/**
 * Cloud Assets (User Stories 80-91).
 *
 * A manual register: every row here was typed or imported. Nothing on this
 * page is synced from a provider, and the UI should never suggest otherwise.
 *
 * Structurally this is the standard management-list page — `Card` shell,
 * `ListTabBar`/`ListToolbar`, advanced-filter drawer, `ResponsiveDataView` —
 * so it reads the same as Assets, Licenses and Inventory (Story 91).
 */
export const CloudAssetsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const { formatCost } = useCurrency();
  const { providers, providerLabel } = useCloudProviders();

  const canCreate = hasPermission('cloud.create');
  const canEdit = hasPermission('cloud.edit');
  const canDelete = hasPermission('cloud.delete');
  const canImport = hasPermission('cloud.import');
  const canExport = hasPermission('cloud.export');
  const canViewRecommendations = hasPermission('cloud.recommendation.view');

  const [activeTab, setActiveTab] = useState('overview');

  const [resources, setResources] = useState<CloudResource[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<CloudStatistics>(EMPTY_STATS);
  const [environments, setEnvironments] = useState<CloudEnvironment[]>([]);
  const [resourceTypes, setResourceTypes] = useState<CloudResourceType[]>([]);
  const [departments, setDepartments] = useState<{ id: number; name: string }[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  /** Vendor master (User Story 103). Behind `vendors.view`, so a role without
   * it simply gets an empty picker rather than a failed page load. */
  const [vendors, setVendors] = useState<Vendor[]>([]);
  /**
   * Environment tier master (User Story 95). Loaded with `includeInactive` so a
   * record on a retired tier can still display it — the forms filter down to
   * active plus current, and the filter drawer wants the full list.
   */
  const [tiers, setTiers] = useState<CloudTier[]>([]);
  const [currencies, setCurrencies] = useState<string[]>(['INR']);
  const [lifecycleStatuses, setLifecycleStatuses] = useState<
    { label: string; value: string }[]
  >([]);

  const [listLoading, setListLoading] = useState(true);
  const [environmentsLoading, setEnvironmentsLoading] = useState(true);

  const [showFilters, setShowFilters] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<CloudResource | null>(null);
  const [detailResource, setDetailResource] = useState<CloudResource | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CloudResource | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteReasonTouched, setDeleteReasonTouched] = useState(false);

  /** The settled table state → the API query. One place, so the list fetch and
   * the export always narrow by exactly the same thing. */
  const buildQuery = (
    s: ManagementTableState<CloudAdvancedFilterState>,
  ): CloudResourceQuery => ({
    ...s.filters,
    search: s.search || undefined,
    sortBy: s.sortBy,
    sortDir: s.sortDir ? (s.sortDir.toUpperCase() as 'ASC' | 'DESC') : undefined,
    page: s.page,
    limit: s.pageSize,
  });

  /** Fetches the current page for a settled query state — the single fetch
   * trigger the shared hook calls after every user action. The AbortSignal
   * cancels a superseded request, and the rows underneath stay mounted
   * (only `listLoading` toggles a dim overlay) so nothing blinks. */
  const loadResourcesForState = async (
    s: ManagementTableState<CloudAdvancedFilterState>,
    signal: AbortSignal,
  ) => {
    setListLoading(true);
    try {
      const [list, statistics] = await Promise.all([
        cloudAssetService.getResources(buildQuery(s), signal),
        cloudAssetService.getStatistics(signal),
      ]);
      setResources(list.data);
      setTotal(list.total);
      setStats(statistics);
    } catch (error: any) {
      // Superseded by a newer request, or the component unmounted — not a failure.
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
      showToast(error?.friendlyMessage || 'Failed to load cloud resources', 'error');
    } finally {
      if (!signal.aborted) setListLoading(false);
    }
  };

  const qts = useManagementTableState<CloudAdvancedFilterState>({
    emptyFilters: EMPTY_CLOUD_FILTERS,
    defaultTab: 'all',
    defaultPageSize: 25,
    onStateChange: loadResourcesForState,
  });

  const loadEnvironments = useCallback(async () => {
    setEnvironmentsLoading(true);
    try {
      setEnvironments(await cloudAssetService.getEnvironments());
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Failed to load cloud environments', 'error');
    } finally {
      setEnvironmentsLoading(false);
    }
    // showToast comes from context and is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Reference data comes from several endpoints with different permissions
   * behind them. allSettled, not all: one 403 must not blank the whole page.
   */
  const loadReferenceData = useCallback(async () => {
    const [types, lifecycles, depts, userList, currencyList, tierList, vendorList] =
      await Promise.allSettled([
        cloudAssetService.getResourceTypes(),
        cloudAssetService.getLifecycleStatuses(),
        departmentsService.getAll(),
        userService.getUsers(),
        currencyService.getActive(),
        cloudAssetService.getTiers(true),
        masterService.getVendors(),
      ]);
    if (vendorList.status === 'fulfilled') setVendors(vendorList.value);
    if (tierList.status === 'fulfilled') setTiers(tierList.value);
    if (types.status === 'fulfilled') setResourceTypes(types.value);
    if (lifecycles.status === 'fulfilled') setLifecycleStatuses(lifecycles.value);
    if (depts.status === 'fulfilled') setDepartments(depts.value as any);
    if (userList.status === 'fulfilled') setUsers(userList.value as any);
    if (currencyList.status === 'fulfilled') {
      const codes = (currencyList.value as any[]).map((c) => c.code);
      if (codes.length) setCurrencies(codes);
    }
  }, []);

  useEffect(() => {
    loadEnvironments();
    loadReferenceData();
  }, [loadEnvironments, loadReferenceData]);

  const lifecycleLabel = useCallback(
    (value: string) =>
      lifecycleStatuses.find((status) => status.value === value)?.label ?? value,
    [lifecycleStatuses],
  );

  /** Refetches the list without changing the query — used after a mutation. */
  const reloadList = useCallback(() => {
    const controller = new AbortController();
    loadResourcesForState(qts.state, controller.signal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qts.state]);

  // ─── Filter option lists + chips ──────────────────────────────────────────

  const providerOptions: MultiSelectOption[] = useMemo(
    () => providers.map((p) => ({ value: p.code, label: p.name })),
    [providers],
  );
  const providerStateOptions: MultiSelectOption[] = useMemo(
    () =>
      Object.entries(CLOUD_PROVIDER_STATE_LABELS).map(([value, label]) => ({
        value,
        label,
      })),
    [],
  );
  const environmentOptions: MultiSelectOption[] = useMemo(
    () => environments.map((e) => ({ value: String(e.id), label: e.name })),
    [environments],
  );
  const resourceTypeOptions: MultiSelectOption[] = useMemo(
    () => resourceTypes.map((t) => ({ value: String(t.id), label: t.normalizedType })),
    [resourceTypes],
  );
  const lifecycleStatusOptions: MultiSelectOption[] = useMemo(
    () => lifecycleStatuses.map((s) => ({ value: s.value, label: s.label })),
    [lifecycleStatuses],
  );
  const departmentOptions: MultiSelectOption[] = useMemo(
    () => departments.map((d) => ({ value: String(d.id), label: d.name })),
    [departments],
  );

  const filterChips: FilterChip[] = useMemo(() => {
    const applied = qts.state.filters;
    const chips: FilterChip[] = [];

    /** One chip per selected value; removing it drops just that value. */
    const addList = <T extends string | number>(
      key: keyof CloudAdvancedFilterState,
      values: T[] | undefined,
      label: (value: T) => string,
    ) => {
      (values ?? []).forEach((value) => {
        chips.push({
          key: `${String(key)}-${value}`,
          label: label(value),
          onRemove: () => {
            const next = (values ?? []).filter((v) => v !== value);
            qts.setFilters({
              ...applied,
              [key]: next.length ? next : undefined,
            });
          },
        });
      });
    };

    addList('provider', applied.provider, (v) => providerLabel(String(v)));
    addList(
      'environmentId',
      applied.environmentId,
      (v) => environments.find((e) => e.id === v)?.name ?? `Environment ${v}`,
    );
    addList(
      'resourceTypeId',
      applied.resourceTypeId,
      (v) => resourceTypes.find((t) => t.id === v)?.normalizedType ?? `Type ${v}`,
    );
    addList(
      'providerState',
      applied.providerState,
      (v) => CLOUD_PROVIDER_STATE_LABELS[v as never] ?? v,
    );
    addList('lifecycleStatus', applied.lifecycleStatus, (v) => lifecycleLabel(v));
    addList(
      'departmentId',
      applied.departmentId,
      (v) => departments.find((d) => d.id === v)?.name ?? `Department ${v}`,
    );
    addList('region', applied.region, (v) => `Region: ${v}`);
    addList('environmentTier', applied.environmentTier, (v) => `Tier: ${v}`);

    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    qts.state.filters,
    environments,
    resourceTypes,
    departments,
    lifecycleLabel,
  ]);

  // ─── Mutations ────────────────────────────────────────────────────────────

  const deleteReasonError =
    deleteReasonTouched && deleteReason.trim().length < 5
      ? 'Reason must be at least 5 characters.'
      : undefined;

  const openDeleteConfirm = (resource: CloudResource) => {
    setPendingDelete(resource);
    setDeleteReason('');
    setDeleteReasonTouched(false);
  };

  const confirmDelete = async () => {
    if (!pendingDelete || deleteReason.trim().length < 5) return;
    const resource = pendingDelete;
    setPendingDelete(null);
    try {
      await cloudAssetService.deleteResource(resource.id, deleteReason.trim());
      showToast('Cloud resource deleted', 'success');
      reloadList();
      loadEnvironments();
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Failed to delete resource', 'error');
    }
  };

  const handleExport = async () => {
    try {
      const rows = await cloudAssetService.getResourcesForExport(
        buildQuery(qts.state),
      );
      exportToCsv(
        `cloud-resources-${new Date().toISOString().slice(0, 10)}.csv`,
        [
          'Provider',
          'Provider Resource ID',
          'Name',
          'Resource Type',
          'Environment',
          'Region',
          'Provider State',
          'Lifecycle Status',
          'Department',
          'Business Owner',
          'Technical Owner',
          'Requested By',
          'Approved By',
          'Environment Tier',
          'Application',
          'Cost Center',
          'Vendor',
          'Monthly Cost',
          'Currency',
          'Cost Last Entered',
        ],
        rows.map((row) => [
          providerLabel(row.provider),
          row.providerResourceId,
          row.name,
          row.resourceType?.normalizedType ?? '',
          row.environment?.name ?? '',
          row.region ?? '',
          CLOUD_PROVIDER_STATE_LABELS[row.providerState] ?? row.providerState,
          lifecycleLabel(row.lifecycleStatus),
          row.department?.name ?? '',
          row.businessOwner
            ? `${row.businessOwner.firstName} ${row.businessOwner.lastName}`
            : '',
          row.technicalOwner
            ? `${row.technicalOwner.firstName} ${row.technicalOwner.lastName}`
            : '',
          row.requestedBy
            ? `${row.requestedBy.firstName} ${row.requestedBy.lastName}`
            : '',
          row.approvedBy
            ? `${row.approvedBy.firstName} ${row.approvedBy.lastName}`
            : '',
          row.environmentTier ?? '',
          row.application ?? '',
          row.costCenter ?? '',
          row.vendor?.name ?? '',
          // Exported raw in the record's own currency — an export must not be
          // silently converted into whatever the viewer happens to be showing.
          row.monthlyCost == null ? '' : formatDecimal(Number(row.monthlyCost)),
          row.currency,
          row.costUpdatedAt ? new Date(row.costUpdatedAt).toLocaleDateString() : '',
        ]),
      );
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Export failed', 'error');
    }
  };

  const rowActions = (resource: CloudResource): ActionItem[] => {
    const actions: ActionItem[] = [
      {
        label: 'View Details',
        icon: <Eye className="h-4 w-4" />,
        onClick: () => setDetailResource(resource),
        variant: 'default',
      },
    ];
    if (canEdit) {
      actions.push({
        label: 'Edit',
        icon: <Pencil className="h-4 w-4" />,
        onClick: () => {
          setEditingResource(resource);
          setFormOpen(true);
        },
        variant: 'default',
      });
    }
    if (canDelete) {
      actions.push({
        label: 'Delete',
        icon: <Trash2 className="h-4 w-4" />,
        onClick: () => openDeleteConfirm(resource),
        variant: 'danger',
      });
    }
    return actions;
  };

  const costCell = (resource: CloudResource) =>
    resource.monthlyCost == null
      ? '—'
      : formatCost(Number(resource.monthlyCost), resource.currency);

  const totalPages = Math.max(1, Math.ceil(total / qts.state.pageSize));

  const tabs = [
    { value: 'overview', label: 'Overview', icon: LayoutDashboard, count: null },
    { value: 'resources', label: 'Resources', icon: Server, count: stats.total },
    {
      value: 'environments',
      label: 'Environments',
      icon: Cloud,
      count: stats.environments,
    },
    { value: 'cost', label: 'Cost', icon: Wallet, count: null },
    ...(canViewRecommendations
      ? [
          {
            value: 'recommendations',
            label: 'Recommendations',
            icon: Lightbulb,
            count: null,
          },
        ]
      : []),
  ];

  const emptyState = (
    <EmptyResourcesState
      hasFilters={qts.hasActiveSearchOrFilters}
      onClearFilters={qts.clearSearchAndFilters}
    />
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cloud Assets"
        description="Cloud resources recorded and maintained by hand — not synced from any provider."
      >
        <div className="flex flex-wrap items-center gap-2">
          {activeTab === 'resources' && (canExport || canImport) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Settings2 className="h-4 w-4" /> Manage Data
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {canExport && (
                  <DropdownMenuItem onClick={handleExport} className="gap-2">
                    <FileSpreadsheet className="h-4 w-4 text-success" />
                    Export to Excel (.csv)
                  </DropdownMenuItem>
                )}
                {canImport && (
                  <>
                    {canExport && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      onClick={() => setImportOpen(true)}
                      className="gap-2"
                    >
                      <Upload className="h-4 w-4 text-primary" />
                      Import from Excel
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {activeTab === 'resources' && canCreate && (
            <Button
              size="sm"
              onClick={() => {
                setEditingResource(null);
                setFormOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Resource
            </Button>
          )}
        </div>
      </PageHeader>

      <Card className="shadow-sm border-muted/20">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <ListTabBar>
            <TabsList className="bg-transparent h-auto p-0 gap-8 border-none flex justify-start">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.value;
                return (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="group relative data-[state=active]:text-primary data-[state=active]:shadow-none rounded-none px-0 pb-3 pt-4 bg-transparent whitespace-nowrap gap-2 text-muted-foreground hover:text-foreground transition-all border-none"
                  >
                    <tab.icon className="h-4 w-4" />
                    <span className="font-semibold text-sm tracking-tight">
                      {tab.label}
                    </span>
                    {tab.count !== null && (
                      <span
                        className={cn(
                          'ml-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold transition-all',
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'bg-muted text-muted-foreground group-hover:bg-muted/80',
                        )}
                      >
                        {tab.count}
                      </span>
                    )}
                    {isActive && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary animate-in fade-in slide-in-from-bottom-1" />
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </ListTabBar>

          <CardContent className="p-0">
            <TabsContent
              value="overview"
              className="mt-0 border-none p-4 focus-visible:ring-0 sm:p-6"
            >
              <CloudOverviewTab />
            </TabsContent>

            <TabsContent
              value="resources"
              className="mt-0 border-none p-0 focus-visible:ring-0"
            >
              <ListToolbar
                searchValue={qts.searchInput}
                onSearchChange={qts.setSearchInput}
                searchPlaceholder="Search name, resource id, region..."
                activeFilterCount={qts.activeFilterCount}
                onOpenFilters={() => {
                  qts.syncDraft();
                  setShowFilters(true);
                }}
                onClearAll={qts.clearSearchAndFilters}
                hasActive={qts.hasActiveSearchOrFilters}
              />

              <FilterChipsBar chips={filterChips} onClearAll={qts.clearFilters} />

              <TableLoadingBar show={listLoading} />

              <ResponsiveDataView
                items={resources}
                className={cn(listLoading && TABLE_DIM_CLASS)}
                label="Cloud resources"
                empty={emptyState}
                toCard={(resource) => ({
                  key: resource.id,
                  eyebrow: (
                    <>
                      <Badge variant="outline">
                        {CLOUD_PROVIDER_STATE_LABELS[resource.providerState]}
                      </Badge>
                      <Badge variant="secondary">
                        {lifecycleLabel(resource.lifecycleStatus)}
                      </Badge>
                    </>
                  ),
                  title: resource.name,
                  subtitle: `${providerLabel(resource.provider)} · ${
                    resource.resourceType?.normalizedType ?? 'Untyped'
                  }`,
                  fields: [
                    { label: 'Environment', value: resource.environment?.name ?? '—' },
                    { label: 'Region', value: resource.region ?? '—' },
                    { label: 'Department', value: resource.department?.name ?? '—' },
                    { label: 'Monthly Cost', value: costCell(resource) },
                  ],
                  actions: <ActionDropdown actions={rowActions(resource)} />,
                  onClick: () => setDetailResource(resource),
                })}
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        onClick={() => qts.toggleSort('name')}
                        className="cursor-pointer transition-colors hover:text-primary"
                      >
                        Name{' '}
                        <ArrowUpDown
                          className={cn(
                            'ml-1 inline h-3 w-3',
                            qts.state.sortBy === 'name' ? 'text-primary' : 'opacity-30',
                          )}
                        />
                      </TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Environment</TableHead>
                      <TableHead>Region</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Lifecycle</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead
                        onClick={() => qts.toggleSort('monthlyCost')}
                        className="cursor-pointer text-right transition-colors hover:text-primary"
                      >
                        Monthly Cost{' '}
                        <ArrowUpDown
                          className={cn(
                            'ml-1 inline h-3 w-3',
                            qts.state.sortBy === 'monthlyCost'
                              ? 'text-primary'
                              : 'opacity-30',
                          )}
                        />
                      </TableHead>
                      <TableHead className="w-[50px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resources.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="py-8">
                          {emptyState}
                        </TableCell>
                      </TableRow>
                    ) : (
                      resources.map((resource) => (
                        <TableRow key={resource.id}>
                          <TableCell>
                            <button
                              type="button"
                              onClick={() => setDetailResource(resource)}
                              className="cursor-pointer text-left font-semibold text-primary hover:underline"
                            >
                              {resource.name}
                            </button>
                            <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                              {resource.providerResourceId}
                            </div>
                          </TableCell>
                          <TableCell>
                            {providerLabel(resource.provider)}
                          </TableCell>
                          <TableCell>
                            {resource.resourceType?.normalizedType ?? '—'}
                          </TableCell>
                          <TableCell>{resource.environment?.name ?? '—'}</TableCell>
                          <TableCell>{resource.region ?? '—'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {CLOUD_PROVIDER_STATE_LABELS[resource.providerState]}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {lifecycleLabel(resource.lifecycleStatus)}
                            </Badge>
                          </TableCell>
                          <TableCell>{resource.department?.name ?? '—'}</TableCell>
                          <TableCell className="text-right font-medium">
                            {costCell(resource)}
                          </TableCell>
                          <TableCell className="text-right">
                            <ActionDropdown actions={rowActions(resource)} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ResponsiveDataView>

              <Pagination
                currentPage={qts.state.page}
                totalPages={totalPages}
                onPageChange={qts.setPage}
                totalItems={total}
                pageSize={qts.state.pageSize}
                onPageSizeChange={qts.setPageSize}
              />
            </TabsContent>

            <TabsContent
              value="environments"
              className="mt-0 border-none p-0 focus-visible:ring-0"
            >
              <CloudEnvironmentsTab
                environments={environments}
                tiers={tiers}
                users={users}
                loading={environmentsLoading}
                onChanged={() => {
                  loadEnvironments();
                  reloadList();
                }}
              />
            </TabsContent>

            <TabsContent
              value="cost"
              className="mt-0 border-none p-4 focus-visible:ring-0 sm:p-6"
            >
              <CloudCostTab />
            </TabsContent>

            {canViewRecommendations && (
              <TabsContent
                value="recommendations"
                className="mt-0 border-none p-0 focus-visible:ring-0"
              >
                <CloudRecommendationsTab
                  // The picker only offers resources already on this page, which is
                  // the scoped set — a recommendation cannot be raised against a
                  // resource the user cannot see.
                  resources={resources}
                  users={users}
                  currencies={currencies}
                />
              </TabsContent>
            )}
          </CardContent>
        </Tabs>
      </Card>

      <CloudAdvancedFilters
        open={showFilters}
        onOpenChange={setShowFilters}
        draft={qts.draftFilters}
        onDraftChange={qts.setDraftFilters}
        onApply={qts.applyDraft}
        onClear={qts.clearFilters}
        providerOptions={providerOptions}
        environmentOptions={environmentOptions}
        resourceTypeOptions={resourceTypeOptions}
        providerStateOptions={providerStateOptions}
        lifecycleStatusOptions={lifecycleStatusOptions}
        tiers={tiers}
        departmentOptions={departmentOptions}
      />

      <CloudResourceFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          reloadList();
          loadEnvironments();
        }}
        resource={editingResource}
        environments={environments}
        tiers={tiers}
        resourceTypes={resourceTypes}
        departments={departments}
        vendors={vendors}
        users={users}
        lifecycleStatuses={lifecycleStatuses}
        currencies={currencies}
      />

      <CloudResourceDetailModal
        open={Boolean(detailResource)}
        onClose={() => setDetailResource(null)}
        resource={detailResource}
        lifecycleLabel={lifecycleLabel}
        providerLabel={providerLabel}
        onEdit={(resource) => {
          setDetailResource(null);
          setEditingResource(resource);
          setFormOpen(true);
        }}
      />

      <CloudImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          reloadList();
          loadEnvironments();
        }}
      />

      <Dialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Cloud Resource</DialogTitle>
            <DialogDescription>
              Delete "{pendingDelete?.name ?? ''}"? This removes it from the register and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <FormField id="deleteReason" label="Reason for deletion" required error={deleteReasonError}>
            <Textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              onBlur={() => setDeleteReasonTouched(true)}
              placeholder="e.g. Decommissioned, duplicate entry, provisioned by mistake..."
              rows={3}
              autoFocus
            />
          </FormField>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteReason.trim().length < 5}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CloudAssetsPage;
